import { useEffect, useRef, useState } from "react";
import { extractQuotationsList, getQuotationVersions } from "../../../services/quotationService";
import {
  syncCreatedVersionLocksFromHistory,
  buildQuotationVersionHistory,
  dedupeQuotationVersions,
  normalizeApiQuotation,
  normalizeStatusLabel,
} from "./quotationUtils";


/**
 * Loads GET /saas/crm/quotations/versions?refId=… when the drawer opens (or on prefetch).
 * Skips repeat fetches for the same refId until the quotation identity changes.
 */
function useLazyQuotationVersionHistory({
  open,
  refId,
  fallbackHistory = [],
  onLoaded,
  prefetch = false,
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const loadedRefIdRef = useRef("");
  const fallbackRef = useRef(fallbackHistory);
  const onLoadedRef = useRef(onLoaded);
  fallbackRef.current = fallbackHistory;
  onLoadedRef.current = onLoaded;

  useEffect(() => {
    const key = String(refId || "").trim();
    if (loadedRefIdRef.current && loadedRefIdRef.current !== key) {
      loadedRefIdRef.current = "";
    }
  }, [refId]);

  useEffect(() => {
    if (!open && !prefetch) return undefined;

    const key = String(refId || "").trim();
    if (!key) {
      if (open) setError("Quotation ref ID is missing.");
      return undefined;
    }
    if (loadedRefIdRef.current === key) return undefined;

    let active = true;

    (async () => {
      setLoading(true);
      setError("");
      try {
        const response = await getQuotationVersions(key);
        if (!active) return;
        const items = extractQuotationsList(response)
          .map((item) => normalizeApiQuotation(item))
          .filter((item) => item?.refId || item?.id != null);

        // API already includes the current version — use it as the sole source of truth.
        // Do not merge/append the locally seeded current quotation from frontend state.
        let nextHistory = [];
        if (items.length) {
          const history = buildQuotationVersionHistory(items, key);
          nextHistory = history.length ? history : dedupeQuotationVersions(items);
        } else {
          const fallback = Array.isArray(fallbackRef.current) ? fallbackRef.current : [];
          nextHistory = buildQuotationVersionHistory(fallback, key);
        }

        // Lock Create New Version on every non-current id (covers existing quotations too).
        syncCreatedVersionLocksFromHistory(nextHistory);
        onLoadedRef.current?.(nextHistory);
        loadedRefIdRef.current = key;
      } catch (err) {
        if (!active) return;
        if (open) setError(err.message || "Failed to load version history.");
      } finally {
        if (active) setLoading(false);
      }
    })();

    return () => {
      active = false;
    };
  }, [open, refId, prefetch]);

  return { loading, error };
}

export {
  useLazyQuotationVersionHistory
};

// Refresh only while import processing is active; clean up on status/route changes.
export function useQuotationAutoRefresh(quotation) {
  useEffect(() => {
    if (normalizeStatusLabel(quotation.status) !== "In Progress") return undefined;
    const timer = window.setInterval(() => window.location.reload(), 10_000);
    return () => window.clearInterval(timer);
  }, [quotation.id, quotation.status]);
}
